import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import { CameraCapture, ScanResultCard } from '../components/scanner/index.ts';
import AddCardModal from '../components/AddCardModal.tsx';
import {
  processForOCR,
  extractNameRegion,
  extractSetNumberRegion,
  canvasToDataURL,
  resizeForAI
} from '../lib/scanner/imageProcessor.ts';
import { recognizeCard, isConfidenceAcceptable, terminateOCR } from '../lib/scanner/ocr.ts';
import { matchFromOCR, CardMatch } from '../lib/scanner/matcher.ts';
import { smartIdentify } from '../lib/scanner/aiIdentifier.ts';
import { addCardInstance } from '../lib/collectionService.ts';
import { CreateCardInstanceData } from '../types/collection.ts';

type ScannerState = 'idle' | 'camera' | 'processing' | 'result' | 'addCard';

export default function Scanner() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState<ScannerState>('idle');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [match, setMatch] = useState<CardMatch | null>(null);
  const [alternateMatches, setAlternateMatches] = useState<CardMatch[]>([]);
  const [confidence, setConfidence] = useState(0);
  const [usedAI, setUsedAI] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCapture = useCallback(async (canvas: HTMLCanvasElement) => {
    setState('processing');
    setError(null);

    try {
      // Save captured image for display
      setCapturedImage(canvasToDataURL(canvas, 0.6));

      // Process image for OCR
      const processed = await processForOCR(canvas);
      const nameCanvas = extractNameRegion(processed.canvas);
      const setCanvas = extractSetNumberRegion(processed.canvas);

      // Run OCR
      const ocrResult = await recognizeCard(nameCanvas, setCanvas);
      console.log('OCR Result:', ocrResult);

      // Try to match against card database
      const matchResult = await matchFromOCR(ocrResult);
      console.log('Match Result:', matchResult);

      // If OCR confidence is low, try AI identification
      if (!isConfidenceAcceptable(ocrResult.confidence) || matchResult.confidence < 60) {
        const aiCanvas = resizeForAI(canvas, 800);
        const imageBase64 = canvasToDataURL(aiCanvas, 0.8);

        const aiResult = await smartIdentify(
          imageBase64.replace(/^data:image\/\w+;base64,/, ''),
          ocrResult.confidence
        );

        if (aiResult.usedAI && aiResult.result) {
          setUsedAI(true);
          // Try to match AI result
          const aiMatchResult = await matchFromOCR({
            name: aiResult.result.cardName,
            confidence: aiResult.result.confidence,
          });

          if (aiMatchResult.bestMatch) {
            setMatch(aiMatchResult.bestMatch);
            setAlternateMatches(aiMatchResult.matches.slice(1));
            setConfidence(aiResult.result.confidence);
            setState('result');
            return;
          }
        }
      }

      // Use OCR result
      setMatch(matchResult.bestMatch);
      setAlternateMatches(matchResult.matches.slice(1));
      setConfidence(matchResult.confidence);
      setUsedAI(false);
      setState('result');

    } catch (err) {
      console.error('Scan error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process image');
      setState('result');
    }
  }, []);

  const handleConfirm = (selectedMatch: CardMatch) => {
    setMatch(selectedMatch);
    setState('addCard');
  };

  const handleSelectAlternate = (selectedMatch: CardMatch) => {
    setMatch(selectedMatch);
    // Boost confidence when user manually selects
    setConfidence(Math.min(95, confidence + 20));
  };

  const handleAddCard = async (data: CreateCardInstanceData) => {
    if (!user) throw new Error('Not authenticated');
    await addCardInstance(user.uid, data);
    // Reset and go back to idle
    resetScanner();
    navigate('/');
  };

  const resetScanner = () => {
    setState('idle');
    setCapturedImage(null);
    setMatch(null);
    setAlternateMatches([]);
    setConfidence(0);
    setUsedAI(false);
    setError(null);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          handleCapture(canvas);
        }
      };
      img.src = e.target?.result as string;
    };

    reader.readAsDataURL(file);
  };

  // Cleanup OCR worker on unmount
  React.useEffect(() => {
    return () => {
      terminateOCR();
    };
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Camera className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Sign in to scan cards</h2>
          <p className="text-gray-400">You need to be signed in to add cards to your collection</p>
        </div>
      </div>
    );
  }

  // Camera view
  if (state === 'camera') {
    return (
      <CameraCapture
        onCapture={handleCapture}
        onClose={() => setState('idle')}
        isProcessing={state === 'processing'}
      />
    );
  }

  // Processing overlay
  if (state === 'processing') {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-6" />
          <p className="text-xl font-medium mb-2">Analyzing card...</p>
          <p className="text-gray-400 text-sm">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  // Result view
  if (state === 'result') {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
        <ScanResultCard
          match={match}
          alternateMatches={alternateMatches}
          confidence={confidence}
          usedAI={usedAI}
          capturedImage={capturedImage || undefined}
          onConfirm={handleConfirm}
          onSelectAlternate={handleSelectAlternate}
          onRetry={() => setState('camera')}
          onCancel={resetScanner}
        />
      </div>
    );
  }

  // Add card modal
  if (state === 'addCard' && match) {
    return (
      <AddCardModal
        card={{
          id: match.cardId,
          name: match.name,
          images: { small: match.imageUrl },
          set: match.setName,
        }}
        onClose={resetScanner}
        onAdd={handleAddCard}
      />
    );
  }

  // Idle state - scanner landing
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 p-4 md:p-6">
      <div className="max-w-md mx-auto pt-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Scan Card</h1>
          <p className="text-gray-400">
            Take a photo of your Pokemon card to add it to your collection
          </p>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg px-4 py-3 text-red-400 text-sm mb-6">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Camera button */}
          <button
            onClick={() => setState('camera')}
            className="w-full flex items-center justify-center gap-3 py-6 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-colors"
          >
            <Camera className="w-8 h-8" />
            <div className="text-left">
              <div className="text-lg font-semibold">Take Photo</div>
              <div className="text-sm text-blue-200">Use your camera to scan</div>
            </div>
          </button>

          {/* Upload button */}
          <label className="w-full flex items-center justify-center gap-3 py-6 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors cursor-pointer">
            <Upload className="w-8 h-8" />
            <div className="text-left">
              <div className="text-lg font-semibold">Upload Image</div>
              <div className="text-sm text-gray-400">Select from your device</div>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
        </div>

        {/* Tips */}
        <div className="mt-8 bg-gray-800/50 rounded-xl p-4">
          <h3 className="font-medium text-white mb-3">Tips for best results:</h3>
          <ul className="space-y-2 text-sm text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              Good lighting - avoid shadows on the card
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              Keep the card flat and centered in frame
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              Make sure the card name is clearly visible
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              Hold steady to avoid blur
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
